import { Menu, Transition } from '@headlessui/react';
import {
    BuildingOfficeIcon,
    CheckIcon,
    ChevronUpDownIcon,
} from '@heroicons/react/24/outline';
import { Fragment, useState } from 'react';

import { selectOrganization } from '../services/organizationService';
import {
    currentOrganization,
    Organization,
    organizations,
    orgSetFromLogin,
} from '../state/organization';

function OrganizationSwitcher() {
    const [isSwitching, setIsSwitching] = useState(false);

    // Access signal values
    const orgs = organizations.value;
    const currentOrg = currentOrganization.value;
    const wasSetFromLogin = orgSetFromLogin.value;

    // Don't render if:
    // - Organization was set from WorkOS login (user selected org at login)
    // - User has 0 or 1 organization
    // - No current org selected
    if (wasSetFromLogin || !currentOrg || orgs.length <= 1) {
        return null;
    }

    const handleSwitch = async (org: Organization) => {
        if (org.id === currentOrg.id || isSwitching) return;

        setIsSwitching(true);

        try {
            await selectOrganization(org.id);
            // Reload page to refresh all data with new organization context
            window.location.reload();
        } catch (error) {
            console.error('[ORG-SWITCHER] Failed to switch organization:', error);
            setIsSwitching(false);
        }
    };

    const getRoleBadgeColor = (role?: string) => {
        switch (role?.toLowerCase()) {
            case 'admin':
            case 'owner':
                return 'bg-purple-100 text-purple-700';
            case 'member':
                return 'bg-blue-100 text-blue-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    return (
        <div className="px-3 py-2 border-b border-gray-200">
            <Menu as="div" className="relative">
                <Menu.Button
                disabled={isSwitching}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
                <BuildingOfficeIcon className="h-4 w-4 text-gray-500 shrink-0" />
                <span className="flex-1 truncate font-medium text-gray-700">
                    {currentOrg.name}
                </span>
                {isSwitching ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-indigo-500" />
                ) : (
                    <ChevronUpDownIcon className="h-4 w-4 text-gray-400 shrink-0" />
                )}
            </Menu.Button>

            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Menu.Items className="absolute left-0 right-0 mt-1 origin-top rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-20 max-h-60 overflow-auto">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Switch Organization
                    </div>
                    {orgs.map((org: Organization) => (
                        <Menu.Item key={org.id}>
                            {({ active }) => (
                                <button
                                    onClick={() => handleSwitch(org)}
                                    disabled={org.id === currentOrg.id}
                                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm ${
                                        active ? 'bg-gray-50' : ''
                                    } ${org.id === currentOrg.id ? 'bg-indigo-50' : ''}`}
                                >
                                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 shrink-0">
                                        <BuildingOfficeIcon className="h-4 w-4 text-gray-600" />
                                    </div>
                                    <div className="flex-1 min-w-0 text-left">
                                        <p className="font-medium text-gray-900 truncate">
                                            {org.name}
                                        </p>
                                        {org.role && (
                                            <span
                                                className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium ${getRoleBadgeColor(org.role)}`}
                                            >
                                                {org.role}
                                            </span>
                                        )}
                                    </div>
                                    {org.id === currentOrg.id && (
                                        <CheckIcon className="h-4 w-4 text-indigo-600 shrink-0" />
                                    )}
                                </button>
                            )}
                        </Menu.Item>
                    ))}
                </Menu.Items>
            </Transition>
        </Menu>
        </div>
    );
}

export default OrganizationSwitcher;
